import "@src/components/surveys/surveys.css";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { getSurveyByTokenQuery } from "@src/util/query/survey.query";
import { submitSurveyByTokenMutation } from "@src/util/mutation/survey.mutation";
import { SurveyAnswerForm } from "@src/components/surveys/surveyAnswerForm";
import { onForm, SurveyFormFill } from "@src/components/formOnPdf/formOnPdf";

/**
 * /survey/:token - the link in a survey email. No login: the token is the key. Customers,
 * suppliers and employees answer here; the page shows the survey, or that it's already been
 * answered, has closed, or the link isn't valid.
 */
export const SurveyAnswerPage = () => {
    const { token } = useParams();
    const { data, loading, error } = useQuery(getSurveyByTokenQuery, { variables: { token }, fetchPolicy: "network-only" });
    const [submit, { loading: submitting }] = useMutation(submitSurveyByTokenMutation);
    const [done, setDone] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const form = data?.getSurveyByToken?.response;

    const send = async (answers, signed) => {
        setSubmitError(null);
        try {
            await submit({ variables: { token, answers, ...(signed || {}) } });
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            setSubmitError(err.message);
        }
    };

    const state = (title, text) => (
        <div className="svy-state">
            <div className="svy-state-title">{title}</div>
            <div className="svy-state-text">{text}</div>
        </div>
    );

    return (
        <div className="svy-page">
            <div className={`svy-card${onForm(form) && form.state === "open" && !done ? " wide" : ""}`}>
                <div className="svy-card-head">
                    <div className="svy-company">{form?.companyName || "Survey"}</div>
                    <div className="svy-title">{form?.title || (loading ? "Loading..." : "Survey")}</div>
                </div>
                <div className="svy-card-body">
                    {loading ? state("Loading...", "")
                        : error || !form ? state("This link isn't working", error?.message || "Please use the link in your email.")
                            : done || form.state === "submitted" ? state("Thank you", done ? "Your answers have been sent." : "You've already answered this survey.")
                                : form.state === "closed" ? state("This survey has closed", "Thank you for your interest - it's no longer taking answers.")
                                    : (
                                        <>
                                            <div className="svy-hint" style={{ marginBottom: 16 }}>
                                                {form.name ? `For ${form.name}. ` : ""}Please answer by {dayjs(form.closesOn).format("D MMMM YYYY")}.
                                                {onForm(form) ? " Fill in the highlighted boxes on the form (Start takes you through them), then read to the end and send." : " It only takes a few minutes."}
                                            </div>
                                            {submitError && <div className="svy-note" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>{submitError}</div>}
                                            {onForm(form)
                                                ? <SurveyFormFill form={form} submitting={submitting} onSubmit={send} />
                                                : <SurveyAnswerForm form={form} submitting={submitting} onSubmit={send} />}
                                        </>
                                    )}
                </div>
            </div>
            <div className="svy-footer">Sent with WFC360</div>
        </div>
    );
};
